import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import ConfirmChangesDialog from './ConfirmChangesDialog';

const BROWSER_BACK = '__browser_back__';

// Intercepts navigation and optionally shows a confirmation dialog.
//
// When isBlocking = true:
//   Link clicks, browser back/forward, and page refresh are intercepted.
//   A confirmation dialog appears; navigation only proceeds if the user confirms.
//
// When isBlocking = false:
//   onNavigate is called on any detected navigation (links, back/forward)
//   so the parent can clean up page state. Navigation proceeds normally.
//
// skipNextNavigationRef (optional):
//   A ref provided by NavigationBlockerContext. When set to true before a
//   programmatic navigation (e.g. window.location.replace), the next
//   popstate/hashchange event is allowed through without showing the dialog.
//   This is necessary because programmatic hash changes fire browser events
//   synchronously — before React state (isBlocking) can be updated — so a
//   ref-based flag is the only way to bypass the blocker in time.
//   The guard history entry is also marked as consumed to prevent the cleanup
//   effect from calling history.back() on unmount.
const NavigationBlocker = ({ isBlocking = true, onNavigate = () => {}, skipNextNavigationRef }) => {
    const [pendingNavigation, setPendingNavigation] = useState(null);

    // Whether a dummy history entry ("guard") is currently in the history stack
    const guardActiveRef = useRef(false);
    // Suppresses the next browser event caused by our own programmatic navigation
    const navigatingRef = useRef(false);

    // --- Guard management ---
    // Push a dummy history entry when blocking starts so the browser "back" button
    // pops it (triggering popstate) instead of leaving the page.
    // Pop it when blocking stops so it doesn't linger in history.
    useEffect(() => {
        navigatingRef.current = false;

        if (isBlocking && !guardActiveRef.current) {
            window.history.pushState(null, '');
            guardActiveRef.current = true;
        } else if (!isBlocking && guardActiveRef.current) {
            navigatingRef.current = true;
            window.history.back();
            guardActiveRef.current = false;
        }
    }, [isBlocking]);

    // --- Event listeners ---
    useEffect(() => {
        // Clicks on hash-based links (sidebar, logo, top bar menu items, etc.)
        // Captured in the capture phase before Angular or the React router process them.
        const handleClick = e => {
            const link = e.target.closest('a[href^="#"]');
            if (!link) return;

            const href = link.getAttribute('href');
            if (!href || href.length <= 1 || href === window.location.hash) return;

            if (!isBlocking) {
                onNavigate();
                return;
            }

            e.preventDefault();
            e.stopImmediatePropagation();
            setPendingNavigation(href);
        };

        // Browser back/forward button — pops the guard entry when blocking
        const handlePopState = () => {
            if (navigatingRef.current) {
                navigatingRef.current = false;
                return;
            }

            // Programmatic navigation bypass — see skipNextNavigationRef above
            if (skipNextNavigationRef?.current) {
                skipNextNavigationRef.current = false;
                guardActiveRef.current = false;
                return;
            }

            if (isBlocking) {
                guardActiveRef.current = false;
                setPendingNavigation(BROWSER_BACK);
            } else {
                onNavigate();
            }
        };

        // Hash changes not initiated by link clicks (back/forward in a hash router).
        // Acts as a fallback alongside popstate for maximum browser compatibility.
        const handleHashChange = () => {
            if (navigatingRef.current) {
                navigatingRef.current = false;
                return;
            }

            // Programmatic navigation bypass — see skipNextNavigationRef above
            if (skipNextNavigationRef?.current) {
                skipNextNavigationRef.current = false;
                guardActiveRef.current = false;
                return;
            }

            if (!isBlocking) {
                onNavigate();
            }
        };

        // Native browser dialog on page refresh / tab close (custom dialogs not possible)
        const handleBeforeUnload = e => {
            if (isBlocking) e.preventDefault();
        };

        document.addEventListener('click', handleClick, true);
        window.addEventListener('popstate', handlePopState);
        window.addEventListener('hashchange', handleHashChange);
        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            document.removeEventListener('click', handleClick, true);
            window.removeEventListener('popstate', handlePopState);
            window.removeEventListener('hashchange', handleHashChange);
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [isBlocking, onNavigate, skipNextNavigationRef]);

    const handleAccept = () => {
        navigatingRef.current = true;

        if (pendingNavigation === BROWSER_BACK) {
            // Guard was already popped by the back button; go back once more for the real navigation
            window.history.back();
        } else if (guardActiveRef.current) {
            // Replace the guard entry with the destination so it doesn't linger in history
            window.location.replace(pendingNavigation);
        } else {
            window.location.href = pendingNavigation;
        }

        guardActiveRef.current = false;
        setPendingNavigation(null);
        onNavigate();
    };

    const handleReject = () => {
        setPendingNavigation(null);

        // Re-push guard so the back button is blocked again
        if (pendingNavigation === BROWSER_BACK) {
            window.history.pushState(null, '');
            guardActiveRef.current = true;
        }
    };

    return <ConfirmChangesDialog open={!!pendingNavigation} onAccept={handleAccept} onReject={handleReject} />;
};

NavigationBlocker.propTypes = {
    isBlocking: PropTypes.bool,
    onNavigate: PropTypes.func,
    skipNextNavigationRef: PropTypes.shape({ current: PropTypes.bool }),
};

export default NavigationBlocker;
